import React from 'react';
import { useTranslation } from 'react-i18next';
import { floorplannerStore } from '../../store/floorplannerStore';

import { RulerLineType } from '../../types/floorplannerTypes';
import { Vector2 } from 'three';
import { editorStore } from '../../store/editorStore';
import { IoClose } from 'react-icons/io5';
import { observer } from 'mobx-react-lite';
import AreaIcon from '../../../src/assets/Measure area new-01.png'
import RulerIcon from '../../assets/symbols/Measure length_Rityta 1.png';
interface DropdownProps {
  position: { top: number; left: number };
  onClose: () => void;
  dropdownRef: React.Ref<HTMLDivElement>;
}

export const MeasurePanel: React.FC<DropdownProps> = observer(({ position, onClose, dropdownRef }) => {
  const { t } = useTranslation();
  const measure2DDisabled = editorStore.view3D;
  const ruler3DDisabled = !editorStore.view3D;

  // Prevent clicks inside the panel from closing it
  const handlePanelClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();  // Prevents the event from bubbling up
  };

  const handleIconClick = (label: string) => {
    if (editorStore.view3D && (label === "AreaConstructionMode" || label === "RulerConstructionMode")) {
      return;
    }

    // Retrieve the visible center from the editor store
    const [centerX, centerY] = editorStore.getVisibleCenter();
    if (label === "RulerLine") {
      // Set a default line length, for example 5 units (adjust based on your needs)
      const rulerLineLength = 2;
      // Calculate the start and end points of the wall, centered around the visible center
      const startX = centerX - rulerLineLength / 2;
      const endX = centerX + rulerLineLength / 2;
      const startY = centerY;
      const endY = centerY;

      const line: RulerLineType = {
        id: floorplannerStore.generateId(),
        type: "rulerLine",
        start: new Vector2(startX, startY), // Start point to the left of the center
        end: new Vector2(endX, endY), // End point to the right of the center
      }
      floorplannerStore.addRulerLine(line);
      const addedLine = floorplannerStore.rulerLinesMap.get(line.id);
      if (addedLine) {
        editorStore.clearSelections();
        floorplannerStore.selectRuler(addedLine.id);
        editorStore.addSelection(addedLine);
      }
      onClose();
    } else if (label === "Ruler3DConstructionMode") {
      editorStore.clearSelections();
      editorStore.setRuler3DConstructionMode(true);
      onClose();
    }  else if (label === "RulerConstructionMode") {
      editorStore.setRulerConstructionMode(!editorStore.rulerConstructionMode);
      onClose();
    }  else if (label === "AreaConstructionMode") {
      editorStore.setAreaConstructionMode(!editorStore.areaConstructionMode);
      onClose();
    }  else if (label === "AreaSymbol") {
      floorplannerStore.addSymbol("area", [0, 0], {
        vertices: [
          new Vector2(0, 0),
          new Vector2(1, 0),
          new Vector2(1.5, 0.5),
          new Vector2(1, 1),
          new Vector2(0, 1),
        ],
      });
    }
  };

  return (
    <div
        className="fixed z-50 w-[388px] bg-white rounded shadow-lg mb-8 overflow-y-auto scrollbar"
      style={{ top: position.top, left: position.left, maxHeight: '70vh' }}
      ref={dropdownRef}
      onClick={handlePanelClick}
    >
      <div className="flex flex-cols items-center border-b border-[#edebe6] justify-between p-2 px-4 pr-2">
        <div className="text-sm font-normal">{t('panels.measuringToolsLibrary')}</div>
        <div onClick={onClose} className="text-sm text-gray-500"><IoClose style={{ strokeWidth: 0.5 }} size={24} /></div>
      </div>
      {/* Group 1 */}
      <div className="mb-4 p-2 px-4">
        <div className=" border-gray-300 pb-2 mb-2 mt-4 text-sm font-medium ">{t('panels.measuringTools')}</div>
       
        <div className="w-[364px] h-[239px] pr-2 grid grid-cols-3 gap-2">
          <button
            type="button"
            className="relative flex h-[114px] w-[114px] cursor-pointer flex-col items-center rounded border border-[#edebe6] bg-white disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-40"
            onClick={() => handleIconClick("AreaConstructionMode")}
            disabled={measure2DDisabled}
            title={measure2DDisabled ? t('common.unavailableIn3d') : t('measure.measureArea')}
          >
            <img className="w-[108px] h-[87px] left-[3px] top-[3px] absolute rounded-sm" src={AreaIcon} alt="" />
            <div className="left-[6px] top-[94px] absolute text-black text-xs font-normal font-['DM Sans']">{t('measure.measureArea')}</div>
          </button>

          <button
            type="button"
            className="relative flex h-[114px] w-[114px] cursor-pointer flex-col items-center rounded border border-[#edebe6] bg-white disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-40"
            onClick={() => handleIconClick("RulerConstructionMode")}
            disabled={measure2DDisabled}
            title={measure2DDisabled ? t('common.unavailableIn3d') : t('measure.measuringStick')}
          >
            <img className="w-[108px] h-[87px] left-[3px] top-[3px] absolute rounded-sm" src={RulerIcon} alt="" />
            <div className="left-[6px] top-[94px] absolute text-black text-xs font-normal font-['DM Sans']">{t('measure.measuringStick')}</div>
          </button>
          <button
            type="button"
            className="relative flex h-[114px] w-[114px] cursor-pointer flex-col items-center rounded border border-[#edebe6] bg-white disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-40"
            onClick={() => handleIconClick("Ruler3DConstructionMode")}
            disabled={ruler3DDisabled}
            title={ruler3DDisabled ? t('common.unavailableIn2d') : t('measure.ruler3dHint')}
          >
            <img className="w-[108px] h-[87px] left-[3px] top-[3px] absolute rounded-sm" src={RulerIcon} alt="" />
            <div className="left-[6px] right-[6px] top-[94px] absolute text-black text-xs font-normal font-['DM Sans'] whitespace-nowrap">{t('measure.ruler3d')}</div>
          </button>
        </div>
      </div>

    </div>

  );
});
